<template>
  <div class="results-dispute">
    <div class="back-row" @click="goBack">
      <i class="el-icon-arrow-left"></i>
      <span>Back to Service Tickets</span>
    </div>

    <div class="header">
      <p class="title">Results Dispute</p>
      <p class="subtitle">
        Use this ticket to flag a result you believe is incorrect — for
        example a score, an award decision, or how the rubric was applied.
        Our staff will review your submission and respond.
      </p>
      <div class="fee-box">
        <div>
          <div class="fee-label">Service fee</div>
          <div class="fee-note">No fee for results disputes.</div>
        </div>
        <div class="fee-amount">Free</div>
      </div>
    </div>

    <el-form :model="form" :rules="rules" ref="form" label-position="top" class="form-body">
      <el-form-item label="Which registration is this dispute for?" prop="application_id">
        <el-select
          v-model="form.application_id"
          placeholder="Select a registration"
          style="width: 100%;"
          :loading="appsLoading">
          <el-option
            v-for="app in applications"
            :key="app._id.$id"
            :label="applicationLabel(app)"
            :value="app._id.$id">
          </el-option>
        </el-select>
        <p class="hint" v-if="!appsLoading && !applications.length">
          No registrations found on your account.
        </p>
      </el-form-item>

      <el-form-item label="What is being disputed?" prop="dispute_type">
        <el-select v-model="form.dispute_type" placeholder="Select a category" style="width: 100%;">
          <el-option label="Score calculation" value="score"></el-option>
          <el-option label="Award decision" value="award"></el-option>
          <el-option label="Rubric application" value="rubric"></el-option>
          <el-option label="Other" value="other"></el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="Reason for dispute" prop="reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="5"
          placeholder="Please explain in detail why you believe the result is incorrect. Include specific scores, awards, or rubric items where relevant."></el-input>
      </el-form-item>

      <el-form-item label="Supporting evidence (link)" prop="evidence_url">
        <el-input
          v-model="form.evidence_url"
          placeholder="Optional. Paste a Google Drive / OneDrive / Dropbox link to any supporting files."></el-input>
        <p class="hint">
          Make sure the link is shareable. Our staff cannot retrieve files
          from links that require additional access requests.
        </p>
      </el-form-item>

      <div class="confirm-row" @click="confirmed = !confirmed">
        <img
          class="chk"
          v-if="!confirmed"
          :src="require('../../assets/image/unchecked.png')" />
        <img class="chk" v-else :src="require('../../assets/image/checked.png')" />
        <span>
          I confirm the information above is accurate. I understand staff
          will review the result based on the original rubric and that the
          outcome may or may not change.
        </span>
      </div>

      <div class="actions">
        <el-button @click="goBack">Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">Submit</el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { createServiceTicket, getStudentApplications } from '../../api/eae'
import { getUserId } from '../../utils/store'

export default {
  name: 'ResultsDisputeTicket',
  data() {
    return {
      confirmed: false,
      submitting: false,
      appsLoading: false,
      applications: [],
      form: {
        application_id: '',
        dispute_type: '',
        reason: '',
        evidence_url: '',
      },
      rules: {
        application_id: [{ required: true, message: 'Please select the registration', trigger: 'change' }],
        dispute_type: [{ required: true, message: 'Please select a category', trigger: 'change' }],
        reason: [
          { required: true, message: 'Please describe the dispute', trigger: 'blur' },
          { min: 20, message: 'Please provide at least 20 characters so staff can act on this.', trigger: 'blur' },
        ],
      },
    }
  },
  mounted() {
    this.fetchApplications()
  },
  methods: {
    fetchApplications() {
      const student_id = getUserId()
      if (!student_id) return
      this.appsLoading = true
      getStudentApplications(student_id)
        .then((res) => {
          const list = (res.data && res.data.data) || []
          this.applications = Array.isArray(list) ? list : []
        })
        .finally(() => {
          this.appsLoading = false
        })
    },
    applicationLabel(app) {
      const project = app.application_project || app.project_name || 'Registration'
      const studentName =
        [app.student_lastName_pinyin, app.student_givenName_pinyin].filter(Boolean).join(' ') ||
        [app.student_lastName, app.student_givenName].filter(Boolean).join(' ') ||
        ''
      return studentName ? `${project} — ${studentName}` : project
    },
    goBack() {
      this.$router.push({ path: '/home/serviceTickets' })
    },
    submit() {
      if (!this.confirmed) {
        this.$message.warning('Please confirm the statement before submitting.')
        return
      }
      this.$refs.form.validate((valid) => {
        if (!valid) return
        const payload = {
          dispute_type: this.form.dispute_type,
          reason: this.form.reason,
          evidence_url: this.form.evidence_url,
        }
        this.submitting = true
        createServiceTicket(getUserId(), 'results_dispute', this.form.application_id, payload)
          .then((res) => {
            const data = res.data
            if (!data || data.code !== 0) {
              this.$message.error((data && data.msg) || 'Failed to submit dispute')
              return
            }
            const ticket_id = data.data && data.data._id && data.data._id.$id
            this.$message.success('Dispute submitted. Our staff will review it shortly.')
            if (ticket_id) {
              this.$router.replace({ path: '/home/serviceTickets/' + ticket_id })
            } else {
              this.$router.replace({ path: '/home/serviceTickets' })
            }
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.results-dispute {
  padding: 24px 32px;
  max-width: 860px;
  margin: 0 auto;
}

.back-row {
  color: #ff8e3b;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.header {
  margin-bottom: 24px;

  .title {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;
    margin-bottom: 6px;
  }

  .subtitle {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
  }
}

.fee-box {
  margin-top: 14px;
  padding: 14px 18px;
  background: #eaf6ec;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .fee-label {
    color: #333;
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 2px;
  }

  .fee-note {
    color: #2e7d32;
    font-size: 12px;
  }

  .fee-amount {
    font-size: 20px;
    font-weight: bold;
    color: #2e7d32;
  }
}

.form-body {
  margin-top: 8px;
}

.confirm-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 16px 0 24px;
  padding: 12px 14px;
  background: #fffbf5;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  line-height: 1.5;

  .chk {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 4px;
}
</style>
